import { VEHICLES } from '../../data/vehicles';
import { buildDriverArchiveEntry } from '../helpers/driverArchive';
import { EVENTS } from '../../data/events';
import { generateId } from '../helpers';
import { CATA_REPAIR_COVERAGE, CATA_THEFT_COVERAGE, CATA_CARGO_COVERAGE } from '../../data/insurancePolicy';
import { computeReputationAbsorption } from '../helpers/breakdownRisk';
import { rollAccidentFault, computeAdminRiskMultiplier } from '../helpers/eventRiskMultipliers';
import { computeOverdraftLimit } from '../helpers/overdraftHelper';
import { getFinanceSender } from '../helpers/financeSender';
import { appendFleetOp, createClaimOp, THEFT_PAYOUT_DAYS } from '../helpers/fleetOpsLog';

export const createEventSlice = (set, get) => ({
  pendingEvents: [],
  eventLog: [],
  /**
   * Events the player has chosen to defer (typically vehicle-purchase
   * opportunities they want to evaluate against the current fleet before
   * deciding). Stored separately from `pendingEvents` so the blocking
   * EventPopup only renders truly unresolved events. A floating "Décisions
   * en attente" badge in the layout lets the player resume any deferred
   * event when they're ready.
   */
  postponedEvents: [],

  /**
   * Move the given event from the active queue to the postponed list.
   * Idempotent — silently no-ops if the event isn't in `pendingEvents`.
   * Used by EventPopup's "Plus tard — consulter ma flotte" button on
   * `isUsedVehicleEvent: true` opportunities (occasion, enchère, liquidation,
   * proposition de rachat). The event keeps its full payload (choices,
   * vehicleOptions…) so the resume flow shows the exact same modal.
   */
  postponeEvent: (eventInstanceId) => {
    const state = get();
    const target = state.pendingEvents.find(e => e.instanceId === eventInstanceId);
    if (!target) return;
    set({
      pendingEvents: state.pendingEvents.filter(e => e.instanceId !== eventInstanceId),
      postponedEvents: [...state.postponedEvents, { ...target, postponedAt: state.totalDays }],
    });
  },

  /**
   * Bring a postponed event back to the head of `pendingEvents` so the
   * EventPopup re-opens it. If `eventInstanceId` is omitted, resumes the
   * oldest postponed event (FIFO).
   */
  resumePostponedEvent: (eventInstanceId) => {
    const state = get();
    const list = state.postponedEvents;
    if (list.length === 0) return;
    const idx = eventInstanceId
      ? list.findIndex(e => e.instanceId === eventInstanceId)
      : 0;
    if (idx < 0) return;
    const target = list[idx];
    set({
      postponedEvents: list.filter((_, i) => i !== idx),
      // Prepend so the resumed event is shown next (before any newer event).
      pendingEvents: [target, ...state.pendingEvents],
    });
  },

  /**
   * Pick the most reputation-damaging choice of an event and resolve it with
   * a "communication de crise" buff: rep loss halved (additional 50 % beyond
   * event_rep_mitigation), +2 notoriety, but the company pays a fixed 500 €
   * for the PR initiative. Unlock: crisis_comm (Aïcha Diallo).
   */
  resolveEventCrisisComm: (eventInstanceId) => {
    const state = get();
    const event = state.pendingEvents.find(e => e.instanceId === eventInstanceId);
    if (!event) return;
    let worstIdx = 0;
    let worstRep = 0;
    event.choices.forEach((c, i) => {
      const r = c.reputationChange || 0;
      if (r < worstRep) { worstRep = r; worstIdx = i; }
    });
    set({ _crisisCommActive: true });
    get().resolveEvent(eventInstanceId, worstIdx);
    const after = get();
    set({
      treasury: after.treasury - 500,
      notoriety: Math.max(0, Math.min(100, (after.notoriety || 0) + 2)),
      _crisisCommActive: false,
    });
  },

  resolveEvent: (eventInstanceId, choiceIndex) => {
    const state = get();
    const event = state.pendingEvents.find(e => e.instanceId === eventInstanceId);
    if (!event) return;
    const choice = event.choices[choiceIndex];

    let newTreasury = state.treasury - (choice.cost || 0);
    let newNotifications = [...(state.notifications || [])];
    let repChange = choice.reputationChange || 0;
    let outcomeNotice = null;
    // 18/09/2026 — dossiers sinistres suivis (cf. helpers/fleetOpsLog.js).
    let fleetOpsPatch = null;
    const _logClaimOp = (op) => { fleetOpsPatch = appendFleetOp(fleetOpsPatch || state.fleetOpsLog, op); };

    // ── RANDOM-OUTCOME EVENTS ──────────────────────────────────────────────
    // For events flagged `randomOutcome: true` (e.g. DREAL inspection), the
    // player's choice is a posture (cooperate / hire lawyer) but the actual
    // outcome (clean / minor infraction / major infraction) is drawn from a
    // dice biased by fleet wear and reputation. We override repChange and add
    // a fine here.

    // 09/02/2026 — Q2 Lot I user prod : popups de panne véhicule générés
    // par tickVehicles (id préfixé `evt-breakdown-`). 3 choix possibles :
    //   choix 0 — Réparer immédiatement : coût = wear × baseRepairCost
    //   choix 1 — Déclarer un sinistre : franchise selon coverage
    //   choix 2 — Mettre hors service : 0 coût, véhicule reste `broken`
    //
    // 10/02/2026 (Lot J Bug 1) — Retour user prod : Amina + son Mantor Actrix
    // restaient bloqués en mission après résolution du popup de panne.
    // Cause : le code libérait uniquement `assignedContractId` du véhicule,
    // mais ni le `assignedDriverId` du véhicule, ni le `status`+`assignedVehicleId`
    // du chauffeur, ni le contrat actif (qui restait dans `activeContracts`
    // avec ses pointeurs vers le couple cassé). On nettoie désormais
    // l'ensemble du graphe via `breakdownDriverCleanupId` et
    // `breakdownContractToCancel` appliqués plus bas dans le set() final.
    let breakdownVehicleUpdate = null;
    let breakdownDriverCleanupId = null;
    let breakdownContractToCancel = null;
    if (typeof event.id === 'string' && event.id.startsWith('evt-breakdown-') && event.vehicleId) {
      const veh = state.vehicles.find(v => v.id === event.vehicleId);
      if (veh) {
        const wear = veh.wear || 0;
        // Capturer les liens en cours AVANT modification du véhicule.
        breakdownDriverCleanupId = veh.assignedDriverId || null;
        breakdownContractToCancel = veh.assignedContractId || null;
        // Coût de réparation atelier : 30 % du coût d'achat × ratio usure
        const baseRepair = Math.max(800, Math.round((veh.purchasePrice || 30000) * 0.30 * (wear / 100)));
        if (choiceIndex === 0) {
          // Réparer immédiatement
          newTreasury -= baseRepair;
          breakdownVehicleUpdate = { id: veh.id, status: 'idle', wear: Math.max(10, wear - 60), assignedContractId: null, assignedDriverId: null };
          outcomeNotice = {
            title: 'Véhicule réparé',
            message: `${veh.brand} ${veh.model} sort de l'atelier. Coût : ${baseRepair.toLocaleString('fr-FR')} €. Chauffeur libéré, disponible immédiatement.`,
            type: 'event_resolved_repair',
          };
        } else if (choiceIndex === 1) {
          // Déclarer un sinistre : franchise selon coverage
          const tier = veh.insuranceCoverage || 'standard';
          const deductible = tier === 'premium' ? Math.round(baseRepair * 0.20)
            : tier === 'standard' ? Math.round(baseRepair * 0.50)
            : Math.round(baseRepair * 0.80);
          newTreasury -= deductible;
          breakdownVehicleUpdate = { id: veh.id, status: 'idle', wear: Math.max(10, wear - 70), assignedContractId: null, assignedDriverId: null };
          // 18/09/2026 — dossier sinistre suivi (prise en charge directe par
          // l'assureur : seule la franchise est débitée, pas de virement).
          const bdOp = createClaimOp({
            id: generateId(), vehicle: veh, claimType: 'breakdown', day: state.totalDays,
            repairCost: baseRepair, reimbursement: baseRepair - deductible, franchise: deductible,
            coverageRate: (baseRepair - deductible) / Math.max(1, baseRepair), tier, payoutDays: 0,
          });
          _logClaimOp({ ...bdOp, status: 'paid', creditedDay: state.totalDays, directSettlement: true });
          outcomeNotice = {
            title: 'Sinistre déclaré',
            message: `${veh.brand} ${veh.model} : franchise ${deductible.toLocaleString('fr-FR')} € (couverture ${tier}). Réparation ${baseRepair.toLocaleString('fr-FR')} € prise en charge directement par l'assureur (dossier n°${bdOp.id.slice(-6).toUpperCase()}). Chauffeur libéré.`,
            type: 'event_resolved_insurance_claim',
          };
        } else {
          // Mettre hors service
          breakdownVehicleUpdate = { id: veh.id, status: 'broken', wear: wear, assignedContractId: null, assignedDriverId: null };
          outcomeNotice = {
            title: 'Véhicule hors service',
            message: `${veh.brand} ${veh.model} immobilisé. Chauffeur libéré et contrat suspendu. Réparable plus tard via la fiche véhicule (Flotte).`,
            type: 'event_resolved_offline',
          };
        }
      }
    }

    if (event.randomOutcome && event.id === 'evt-admin-03') {
      // Pedagogical milestone — first DREAL inspection.
      if (typeof get().triggerMilestone === 'function') {
        try { get().triggerMilestone('first_dreal', '/depot'); }
        catch (err) { console.warn('[milestone] first_dreal trigger failed:', err); }
      }
      const avgWear = state.vehicles.length > 0
        ? state.vehicles.reduce((s, v) => s + (v.wear || 0), 0) / state.vehicles.length
        : 0;
      // Base infraction probability 30 % at neutral wear (50 %) and neutral rep (70).
      let infractionProba = 0.30
        + Math.max(0, avgWear - 50) * 0.01
        - (state.reputation - 70) * 0.01;
      const hasLawyer = choiceIndex === 1;
      if (hasLawyer) infractionProba = infractionProba * 0.5;
      // 08/02/2026 — Q2 Lot D : Sauvegarde + audit IT (inv-it-backup-01)
      // réduit la proba d'infraction (traçabilité, conformité documentaire).
      // On applique le multiplicateur admin (×0.70 si IT backup actif, ×… etc.).
      infractionProba = infractionProba * computeAdminRiskMultiplier(state);
      infractionProba = Math.max(0.05, Math.min(0.90, infractionProba));

      const roll = Math.random();
      if (roll > infractionProba) {
        repChange = 2;
        outcomeNotice = {
          title: 'Contrôle DREAL : tout est en règle',
          message: hasLawyer
            ? "L'avocat a rassuré l'inspecteur. Aucune sanction, réputation +2."
            : "L'inspecteur n'a rien trouvé à redire. Réputation +2.",
          type: 'event_resolved_clean',
        };
      } else {
        const isMajor = Math.random() < (hasLawyer ? 0.10 : 0.30);
        // 08/02/2026 — Q2 Lot D : audit IT (inv-it-backup-01) réduit
        // l'amende DREAL de 30 % (mise en conformité documentaire facilitée).
        const itBackup = !!(state.activeInvestments || {})['inv-it-backup-01'];
        const fineMultiplier = itBackup ? 0.70 : 1.0;
        if (isMajor) {
          let fine = hasLawyer
            ? 800 + Math.floor(Math.random() * 400)
            : 1500 + Math.floor(Math.random() * 500);
          fine = Math.round(fine * fineMultiplier);
          newTreasury -= fine;
          repChange = hasLawyer ? -8 : -12;
          outcomeNotice = {
            title: 'Contrôle DREAL : infraction majeure',
            message: `Amende ${fine.toLocaleString('fr-FR')} €${itBackup ? ' (−30 % grâce à l\'audit IT)' : ''}, réputation ${repChange}. Mise en conformité immédiate exigée.`,
            type: 'event_resolved_major',
          };
        } else {
          let fine = hasLawyer
            ? 200 + Math.floor(Math.random() * 200)
            : 400 + Math.floor(Math.random() * 400);
          fine = Math.round(fine * fineMultiplier);
          newTreasury -= fine;
          repChange = hasLawyer ? -2 : -4;
          outcomeNotice = {
            title: 'Contrôle DREAL : infraction mineure',
            message: `Amende ${fine.toLocaleString('fr-FR')} €${itBackup ? ' (−30 % grâce à l\'audit IT)' : ''}, réputation ${repChange}.`,
            type: 'event_resolved_minor',
          };
        }
      }
    }

    // ── ACCIDENT GRAVE — franchise basée sur la couverture réelle ──────────
    // For a major accident, the player's exposure depends on the *actual*
    // insurance coverage tier of their fleet (Basic / Standard / Premium),
    // not a choice made after the fact.
    if (event.randomOutcome && event.id === 'evt-accident-02') {
      const coverageRanks = { basic: 1, standard: 2, premium: 3 };
      const fleet = state.vehicles || [];
      let avgRank = 2; // default = standard
      if (fleet.length > 0) {
        avgRank = fleet.reduce((s, v) => s + (coverageRanks[v.insuranceCoverage || 'standard'] || 2), 0) / fleet.length;
      }
      let baseFranchise = avgRank >= 2.5 ? 1500 : avgRank >= 1.5 ? 4000 : 7000;
      let baseRep = avgRank >= 2.5 ? -10 : avgRank >= 1.5 ? -15 : -20;
      const hasLawyer = choiceIndex === 1;
      if (hasLawyer) {
        baseFranchise = Math.round(baseFranchise * 0.70);
        baseRep = Math.round(baseRep * 0.70);
      }
      newTreasury -= baseFranchise;
      repChange = baseRep;
      const tierLabel = avgRank >= 2.5 ? 'Premium' : avgRank >= 1.5 ? 'Standard' : 'Basique';
      outcomeNotice = {
        title: hasLawyer ? 'Accident grave : avocat engagé' : 'Accident grave : franchise appliquée',
        message: hasLawyer
          ? `L'avocat a réduit la franchise. Couverture ${tierLabel} — franchise ${baseFranchise.toLocaleString('fr-FR')} €, réputation ${baseRep}.`
          : `Couverture flotte ${tierLabel} — franchise ${baseFranchise.toLocaleString('fr-FR')} €, réputation ${baseRep}.`,
        type: 'event_resolved_accident',
      };
    }

    // ── INFRACTION CODE — contestation tirée aléatoirement ─────────────────
    if (event.randomOutcome && event.id === 'evt-accident-04') {
      if (choiceIndex === 0) {
        outcomeNotice = {
          title: 'Amende réglée',
          message: 'Amende payée. Aucun risque, aucune complication.',
          type: 'event_resolved_clean',
        };
      } else {
        const hasLawyer = !!(state.hiredSupport || {})['role-isabelle-fontaine']
          || !!(state.hiredSupport || {})['role-paul-renaud']
          || !!(state.hiredSupport || {})['role-juriste'];
        let successProba = 0.50 + Math.max(-0.20, Math.min(0.20, (state.reputation - 70) * 0.005));
        if (hasLawyer) successProba += 0.15;
        successProba = Math.max(0.10, Math.min(0.90, successProba));
        if (Math.random() < successProba) {
          repChange = 1;
          outcomeNotice = {
            title: 'Contestation réussie',
            message: hasLawyer
              ? `L'avocat a fait annuler l'amende. Frais de dossier 50 € seulement, réputation +1.`
              : `La contestation a abouti. Frais de dossier 50 € seulement, réputation +1.`,
            type: 'event_resolved_clean',
          };
        } else {
          newTreasury -= 200;
          repChange = -3;
          outcomeNotice = {
            title: 'Contestation rejetée',
            message: `Contestation refusée — amende 200 € + frais de dossier 50 € (total 250 €), réputation -3.`,
            type: 'event_resolved_minor',
          };
        }
      }
    }

    // Apply bonus revenue only if the player actually has the operational
    // capacity (at least one idle vehicle + one available driver). Otherwise
    // log a notification and skip the bonus — fixes the "appel d'offre +2000 €
    // si véhicule disponible" inconsistency where the bonus was applied
    // unconditionally.
    if (choice.bonusRevenue) {
      const requiresCapacity = (choice.description || '').toLowerCase().includes('si véhicule');
      if (requiresCapacity) {
        const idleVehicle = state.vehicles.some(v => v.status === 'idle');
        const freeDriver = state.drivers.some(d => {
          if (d.status === 'training') return false;
          if (d.assignedVehicleId) {
            const v = state.vehicles.find(vv => vv.id === d.assignedVehicleId);
            if (v && v.status === 'active') return false;
          }
          return true;
        });
        if (idleVehicle && freeDriver) {
          newTreasury += choice.bonusRevenue;
        } else {
          newNotifications.push({
            id: generateId(),
            type: 'opportunity_missed',
            title: 'Opportunité manquée',
            message: `Vous n'avez pas pu honorer "${event.name}" — aucun véhicule ou chauffeur disponible. Le bonus de ${choice.bonusRevenue.toLocaleString('fr-FR')} € est perdu.`,
            day: state.totalDays,
            read: false,
          });
        }
      } else {
        newTreasury += choice.bonusRevenue;
      }
    }
    // 08/02/2026 — Q2 Lot B user prod : si le choix (ou l'event) déclenche
    // un booster de pannes (Canicule, Verglas continuer prudemment…), on
    // pose `breakdownBoostUntil = totalDays + 7` qui est lu par
    // computeBreakdownRiskMultiplier pour appliquer ×1.25 pendant 7 j.
    const triggersBoost = !!(choice?.triggersBreakdownBoost || event?.triggersBreakdownBoost);
    if (triggersBoost) {
      const today = state.totalDays || 0;
      const expiresOn = today + 7;
      // On prolonge le booster si déjà actif (cumul = max date).
      const existing = state.breakdownBoostUntil || 0;
      set({ breakdownBoostUntil: Math.max(existing, expiresOn) });
    }
    // 08/02/2026 — Q2 Lot C user prod : événements « Appel d'offre »
    // (evt-opp-01, evt-opp-03) injectent désormais un tender ad-hoc dans
    // le board AO au lieu d'appliquer un bonus immédiat. La narrative se
    // poursuit dans le circuit AO standard (TenderApplicationModal).
    let adHocTenderInjected = null;
    if (choice?.triggersAdHocTender && typeof get().injectAdHocTender === 'function') {
      try {
        const res = get().injectAdHocTender(choice.triggersAdHocTender, { expiryDays: 15 });
        if (res?.ok) {
          adHocTenderInjected = {
            clientName: choice.triggersAdHocTender.clientName,
            tenderId: choice.triggersAdHocTender.id,
            instanceId: res.instanceId,
          };
        }
      } catch (err) { console.warn('[event] injectAdHocTender failed:', err); }
    }

    // 08/02/2026 — Q2 Lot D user prod : Boom sectoriel tangible. Le choix
    // « Saisir toutes les opportunités » sur evt-marche-03 pose un
    // marketBoom dans le state avec startDay/endDay + revenueBoost.
    // Tiré d'un panier de secteurs thématiques pour donner du flavor.
    let marketBoomPatch = null;
    if (choice?.appliesBoomSectoriel) {
      const SECTORS = [
        { theme: 'distribution', label: 'Grande distribution' },
        { theme: 'agro-bio', label: 'Agroalimentaire bio' },
        { theme: 'industriel', label: 'Industrie & BTP' },
        { theme: 'public-local', label: 'Marchés publics locaux' },
        { theme: 'pharma', label: 'Pharma / médical' },
        { theme: 'export', label: 'Export & international' },
      ];
      const pick = SECTORS[Math.floor(Math.random() * SECTORS.length)];
      const today = state.totalDays || 0;
      const days = event.durationDays || 14;
      marketBoomPatch = {
        theme: pick.theme,
        label: pick.label,
        startDay: today,
        endDay: today + days,
        revenueBoost: 0.20,
      };
    }
    // Apply event reputation mitigation from hired support roles to negative changes only.
    // 08/02/2026 — Q2 Lot A : on cumule l'absorption GLOBALE des
    // fonctions support (computeReputationAbsorption) avec l'ancien
    // getSupportEventRepMitigation (rétro-compat). Plafonné à 0.90 pour ne
    // pas annuler complètement l'effet.
    // 08/02/2026 — Q2 Lot D : sur les accidents `faultBased`, la pénalité
    // de réputation N'EST APPLIQUÉE qu'en cas de faute du chauffeur
    // (rollAccidentFault). Sinon (météo, équipement, fatigue extrême =
    // responsabilité entreprise), on annule le malus client mais on
    // garde le coût matériel.
    let accidentFaultInfo = null;
    if (event.faultBased && repChange < 0) {
      accidentFaultInfo = rollAccidentFault(state);
      if (!accidentFaultInfo.atFault) {
        repChange = 0;
      }
    }
    if (repChange < 0) {
      // Source de vérité unifiée (08/02/2026)
      const globalAbs = computeReputationAbsorption(state);
      // Legacy mitigation (rétro-compat avec les tests existants)
      const legacyMit = typeof get().getSupportEventRepMitigation === 'function'
        ? get().getSupportEventRepMitigation() : 0;
      const totalAbs = Math.min(0.90, 1 - (1 - globalAbs) * (1 - legacyMit));
      repChange = Math.round(repChange * (1 - totalAbs));
    }
    // Crisis comm: additional 50 % rep loss reduction on the chosen path.
    if (repChange < 0 && state._crisisCommActive) {
      repChange = Math.round(repChange * 0.5);
    }
    const newReputation = Math.max(0, Math.min(100, state.reputation + repChange));

    let newVehicles = [...state.vehicles];
    let newDrivers = [...state.drivers];

    // 09/02/2026 — Q2 Lot I user prod : applique l'update véhicule décidé
    // par le popup de panne (Réparer / Sinistre / Hors service).
    if (breakdownVehicleUpdate) {
      newVehicles = newVehicles.map(v =>
        v.id === breakdownVehicleUpdate.id ? { ...v, ...breakdownVehicleUpdate } : v
      );
    }
    // 10/02/2026 (Lot J Bug 1) — Nettoyage du chauffeur et du contrat liés
    // au véhicule en panne. Sans ce nettoyage, Amina restait « active »
    // dans le store des chauffeurs et son contrat restait dans
    // `activeContracts` avec pointeurs obsolètes → fantôme dans la liste
    // de validation contrat. La cancellation du contrat est silencieuse
    // (pas de pénalité, l'aléa de panne fait déjà office d'incident).
    if (breakdownDriverCleanupId) {
      newDrivers = newDrivers.map(d =>
        d.id === breakdownDriverCleanupId
          ? { ...d, assignedVehicleId: null, status: 'idle' }
          : d
      );
    }

    let vehicleFinancingPatch = null;
    if (choice.specialAction) {
      const action = choice.specialAction;
      if (action.type === 'buyUsedVehicle') {
        const vehicleData = VEHICLES.find(v => v.id === action.vehicleDataId);
        if (vehicleData) {
          const price = Math.round(vehicleData.price * action.discount);
          const treasuryAfter = newTreasury - price;
          // 13/07/2026 — user prod : un achat comptant qui dépasse le
          // découvert autorisé est incohérent. Dans ce cas, le confrère
          // RÉSERVE le véhicule 10 jours et l'apprenant entre dans l'arc
          // pédagogique « crédit véhicule » (crédit affecté, gagé sur le
          // véhicule — exception à la règle des 3 mois, cf. bankSlice).
          const overdraft = computeOverdraftLimit(state)?.limit || 0;
          if (treasuryAfter < -overdraft) {
            vehicleFinancingPatch = {
              vehicleDataId: vehicleData.id,
              brand: vehicleData.brand,
              model: vehicleData.model,
              price,
              wear: action.wear || 25,
              offeredDay: state.totalDays,
              expiresDay: (state.totalDays || 0) + 10,
              sourceEventId: eventInstanceId,
            };
            newNotifications.push({
              id: generateId(),
              type: 'info',
              title: 'Véhicule réservé — financement à organiser',
              message: `Achat comptant impossible sans dépasser le découvert autorisé (${overdraft.toLocaleString('fr-FR')} €). Le confrère vous réserve le ${vehicleData.brand} ${vehicleData.model} pendant 10 jours : rendez-vous à la BANQUE pour monter un crédit véhicule.`,
              day: state.totalDays,
              read: false,
            });
          } else {
          newTreasury = treasuryAfter;
          newVehicles.push({
            id: generateId(),
            dataId: vehicleData.id,
            brand: vehicleData.brand,
            model: vehicleData.model,
            tier: vehicleData.tier,
            specialties: vehicleData.specialties,
            price: vehicleData.price,
            maintenancePerMonth: vehicleData.maintenancePerMonth,
            insurancePerMonth: vehicleData.insurancePerMonth,
            dailyFuel: vehicleData.dailyFuel,
            wear: action.wear || 25,
            km: Math.round(Math.random() * 80000 + 20000),
            daysActive: 0,
            totalRevenue: 0,
            purchaseDay: state.totalDays,
            status: 'idle',
            assignedDriverId: null,
            assignedContractId: null,
            isUsed: true,
            purchasePrice: price,
          });
          // 18/06/2026 — audit TVA : achat d'occasion → TVA déductible 20 %.
          if (typeof get().recordDeductibleTva === 'function') {
            get().recordDeductibleTva(price, 'vehicle_purchase');
          }
          // Warning notification if treasury went negative
          if (treasuryAfter < 0) {
            newNotifications.push({
              id: generateId(),
              type: 'warning',
              title: 'Trésorerie négative',
              message: `Achat de ${vehicleData.brand} ${vehicleData.model} effectué pour ${price.toLocaleString('fr-FR')} €. Votre trésorerie est désormais à ${treasuryAfter.toLocaleString('fr-FR')} €. Des agios s'appliqueront chaque jour.`,
              day: state.totalDays,
              read: false,
            });
          } else {
            newNotifications.push({
              id: generateId(),
              type: 'vehicle_purchased',
              title: 'V\u00e9hicule d\'occasion acquis',
              message: `${vehicleData.brand} ${vehicleData.model} ajout\u00e9 \u00e0 votre flotte pour ${price.toLocaleString('fr-FR')} \u20ac.`,
              day: state.totalDays,
              read: false,
            });
          }
          }
        }
      } else if (action.type === 'sellOldestVehicle') {
        const idle = state.vehicles.filter(v => v.status !== 'active');
        if (idle.length > 0) {
          const oldest = idle.reduce((a, b) => (a.purchaseDay || 0) < (b.purchaseDay || 0) ? a : b);
          const sellPrice = Math.round(oldest.price * action.rate);
          newTreasury += sellPrice;
          newVehicles = newVehicles.filter(v => v.id !== oldest.id);
          newDrivers = newDrivers.map(d =>
            d.assignedVehicleId === oldest.id ? { ...d, assignedVehicleId: null, status: 'idle' } : d
          );
          newNotifications.push({
            id: generateId(),
            type: 'vehicle_sold',
            title: 'V\u00e9hicule vendu',
            message: `${oldest.brand} ${oldest.model} vendu pour ${sellPrice.toLocaleString('fr-FR')} \u20ac.`,
            day: state.totalDays,
            read: false,
          });
        }
      }
    }

    // ── REAL-IMPACT BRIDGE — evt-rh-02 (arrêt maladie chauffeur) ────────────
    // 18/06/2026 (fix user prod « arrêt maladie fantôme ») — le chauffeur
    // ciblé est stampé au tirage (event.sickDriverId) et les DEUX choix le
    // mettent réellement en arrêt :
    //  - Choix 0 (Intérimaire, 500 €) : chauffeur 'sick', mais son véhicule
    //    continue la mission (flag `subcontracted` → revenus maintenus,
    //    binding conservé, reprise auto au retour).
    //  - Choix 1 (Attendre) : chauffeur 'sick', véhicule libéré (idle).
    if (event.id === 'evt-rh-02') {
      // 23/07/2026 — fix « chauffeur en formation qui disparaît » : le
      // fallback newDrivers[0] pouvait cibler un chauffeur en formation /
      // congés / arrêt. Sans cible éligible, l'aléa reste sans effet RH.
      const candidate = (event.sickDriverId && newDrivers.find(d => d.id === event.sickDriverId))
        || newDrivers.find(d => d.status === 'active' || (d.status === 'idle' && !d.onHoliday));
      if (candidate) {
        const sickDays = event.durationDays || 5;
        if (choiceIndex === 0) {
          // Intérimaire : arrêt réel + mission couverte par l'intérim.
          if (candidate.assignedVehicleId) {
            newVehicles = newVehicles.map(v => v.id === candidate.assignedVehicleId ? {
              ...v,
              subcontracted: true,
              subcontractUntil: state.totalDays + sickDays,
              subcontractDailyCost: 0,
              subcontractTotalCost: choice.cost || 0,
            } : v);
          }
          newDrivers = newDrivers.map(d => d.id === candidate.id ? {
            ...d,
            status: 'sick',
            sickUntil: state.totalDays + sickDays,
            // assignedVehicleId conservé : reprise auto au retour d'arrêt.
          } : d);
          if (typeof get().pushMessageFromTemplate === 'function') {
            get().pushMessageFromTemplate('drv-illness-interim-01', {
              driverId: candidate.id,
              name: candidate.name,
              days: sickDays,
              returnDay: state.totalDays + sickDays,
            });
          }
          // Tuto contextuel — première utilisation de l'intérim.
          if (typeof get().triggerContextualTutorial === 'function') {
            get().triggerContextualTutorial('firstInterim');
          }
        } else {
          // Attendre le retour : véhicule libéré.
          if (candidate.assignedVehicleId) {
            newVehicles = newVehicles.map(v => v.id === candidate.assignedVehicleId ? {
              ...v,
              assignedDriverId: null,
              status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          newDrivers = newDrivers.map(d => d.id === candidate.id ? {
            ...d,
            status: 'sick',
            sickUntil: state.totalDays + sickDays,
            assignedVehicleId: null,
          } : d);
          if (typeof get().pushMessageFromTemplate === 'function') {
            get().pushMessageFromTemplate('drv-illness-system-01', {
              driverId: candidate.id,
              name: candidate.name,
              days: sickDays,
              returnDay: state.totalDays + sickDays,
            });
          }
        }
      }
    }

    // ── DÉMISSION (10/07/2026 — fix user prod) — impact réel sur le
    // chauffeur ciblé au tirage (event.resigningDriverId).
    let resignationArchivePatch = null;
    if (event.id === 'evt-rh-01') {
      // 23/07/2026 — fix « chauffeur en formation qui disparaît » : le
      // fallback newDrivers[0] pouvait acter la démission d'un chauffeur en
      // formation (il disparaissait de l'effectif et revenait dans le vivier).
      // Seuls actifs / dispo au dépôt sont éligibles ; sinon aucun départ.
      const target = (event.resigningDriverId && newDrivers.find(d => d.id === event.resigningDriverId))
        || newDrivers.find(d => d.status === 'active')
        || newDrivers.find(d => d.status === 'idle' && !d.onHoliday);
      if (target) {
        if (choiceIndex === 0) {
          // Contre-offre : +300 €/mois PERMANENTS (+ charges), il reste.
          newDrivers = newDrivers.map(d => d.id === target.id ? {
            ...d,
            salaryGross: (d.salaryGross || 0) + 300,
            employerCost: Math.round((d.employerCost || 0) + 300 * 1.42),
            satisfaction: Math.min(100, (d.satisfaction || 0) + 10),
          } : d);
          outcomeNotice = {
            title: `Contre-offre acceptée — ${target.name}`,
            message: `${target.name} reste dans l'entreprise. Son salaire passe à ${((target.salaryGross || 0) + 300).toLocaleString('fr-FR')} €/mois brut (+300 €/mois permanents, charges incluses).`,
            type: 'event_resolved_rh',
          };
        } else {
          // Laisser partir : départ RÉEL — véhicule libéré, archive réembauche.
          if (target.dataId) {
            resignationArchivePatch = {
              ...(state.firedDriversArchive || {}),
              [target.dataId]: buildDriverArchiveEntry(target, state.totalDays || 0),
            };
          }
          if (target.assignedVehicleId) {
            newVehicles = newVehicles.map(v => v.id === target.assignedVehicleId ? {
              ...v,
              assignedDriverId: null,
              status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          newDrivers = newDrivers.filter(d => d.id !== target.id);
          const hadMission = !!target.assignedVehicleId;
          outcomeNotice = {
            title: `Départ acté — ${target.name}`,
            message: hadMission
              ? `${target.name} quitte l'entreprise. Son véhicule est libéré mais le contrat en cours n'a plus de chauffeur — réassignez vite pour éviter les livraisons ratées.`
              : `${target.name} quitte l'entreprise. Vous pourrez le/la réembaucher plus tard (profil archivé).`,
            type: 'event_resolved_rh',
          };
        }
      }
    }

    // ── CATASTROPHES (13/06/2026) — aléas rares mais très impactants ────
    // L'assurance prend tout son sens : remboursement selon la couverture
    // réelle du véhicule ciblé (basic / standard / premium).
    if (event.id === 'evt-cata-01') {
      // Rupture moteur majeure — cible le véhicule le plus usé du parc.
      const candidates = newVehicles.filter(v => v.status !== 'sold' && v.status !== 'selling' && v.status !== 'delivery');
      const target = [...candidates].sort((a, b) => (b.wear || 0) - (a.wear || 0))[0];
      if (target) {
        const repairCost = Math.round((event.costMin || 24000) + Math.random() * ((event.costMax || 36000) - (event.costMin || 24000)));
        const coverage = target.insuranceCoverage || 'standard';
        const useInsurance = choiceIndex === 0;
        const claims = target.insuranceClaims || [];
        const insuranceRefused = useInsurance && claims.length >= 3;
        const rate = (useInsurance && !insuranceRefused) ? (CATA_REPAIR_COVERAGE[coverage] || 0.80) : 0;
        const reimbursement = Math.round(repairCost * rate);
        const playerPays = repairCost - reimbursement;
        newTreasury -= playerPays;
        newVehicles = newVehicles.map(v => v.id === target.id ? {
          ...v,
          wear: Math.max(0, (v.wear || 0) - 40), // moteur remplacé
          insuranceClaims: (useInsurance && !insuranceRefused)
            ? [...claims, { id: generateId(), day: state.totalDays, repairCost, reimbursement, franchise: playerPays, coverageRate: rate, tier: coverage, claimNumber: claims.length + 1 }]
            : claims,
        } : v);
        outcomeNotice = {
          title: `Rupture moteur — ${target.brand} ${target.model}`,
          message: insuranceRefused
            ? `Assurance refusée (3 sinistres max atteints). Réparation intégrale : −${playerPays.toLocaleString('fr-FR')} €.`
            : useInsurance
              ? `Couverture ${coverage} : l'assureur rembourse ${reimbursement.toLocaleString('fr-FR')} €. Reste à charge : −${playerPays.toLocaleString('fr-FR')} €. Moteur remplacé.`
              : `Réparation payée sans assurance : −${playerPays.toLocaleString('fr-FR')} €. Historique de sinistres préservé.`,
          type: 'event_resolved_cata',
        };
      }
    }
    if (event.id === 'evt-cata-02') {
      // Vol de camion — cible un véhicule possédé (non leasé) au hasard.
      const candidates = newVehicles.filter(v => !v.isLeased && v.status !== 'sold' && v.status !== 'selling' && v.status !== 'delivery');
      const target = candidates[Math.floor(Math.random() * candidates.length)];
      if (target) {
        // Valeur vénale : formule de revente +15 pts (l'assureur indemnise
        // au-dessus du prix de revente forcée).
        const ageMonths = (state.totalDays - (target.purchaseDay || 0)) / 30;
        const sellRate = Math.max(0.15, 0.60 - ((target.wear || 0) / 100) * 0.15 - Math.min(ageMonths * 0.01, 0.20));
        const marketValue = Math.round((target.price || 0) * Math.min(0.85, sellRate + 0.15));
        const coverage = target.insuranceCoverage || 'standard';
        const recovered = choiceIndex === 1 && Math.random() < 0.35;
        if (recovered) {
          newVehicles = newVehicles.map(v => v.id === target.id ? { ...v, wear: Math.min(100, (v.wear || 0) + 10) } : v);
          outcomeNotice = {
            title: `Camion retrouvé — ${target.brand} ${target.model}`,
            message: `L'enquête privée a porté ses fruits : le camion est récupéré sur un parking à l'étranger (usure +10 %).`,
            type: 'event_resolved_cata',
          };
        } else {
          const rate = CATA_THEFT_COVERAGE[coverage] || 0.70;
          const reimbursement = Math.round(marketValue * rate);
          // 18/09/2026 — Retour apprenant : « déclaration faite, aucun retour
          // sur l'indemnisation ». Le vol ouvre un DOSSIER suivi (déclaration
          // reçue → en cours → indemnisation versée J+5 via tickFleetOps).
          const theftOp = createClaimOp({
            id: generateId(), vehicle: target, claimType: 'theft', day: state.totalDays,
            repairCost: marketValue, reimbursement, franchise: marketValue - reimbursement, coverageRate: rate, tier: coverage,
          });
          _logClaimOp(theftOp);
          // Libérer le chauffeur éventuel, retirer le véhicule du parc.
          if (target.assignedDriverId) {
            newDrivers = newDrivers.map(d => d.id === target.assignedDriverId
              ? { ...d, assignedVehicleId: null, status: d.status === 'active' ? 'idle' : d.status }
              : d);
          }
          newVehicles = newVehicles.filter(v => v.id !== target.id);
          if (target.assignedContractId) {
            newNotifications.push({
              id: generateId(),
              type: 'cata_theft_contract',
              title: 'Contrat sans véhicule',
              message: `Le contrat assigné au camion volé n'est plus servi — réassignez un véhicule au plus vite pour éviter les livraisons ratées.`,
              day: state.totalDays,
              read: false,
            });
          }
          outcomeNotice = {
            title: `Vol de camion — ${target.brand} ${target.model}`,
            message: `Le camion est introuvable. Déclaration reçue par l'assureur (dossier n°${theftOp.id.slice(-6).toUpperCase()}) : couverture ${coverage}, indemnisation prévue ${reimbursement.toLocaleString('fr-FR')} € (valeur vénale ${marketValue.toLocaleString('fr-FR')} €) — versement sous ${THEFT_PAYOUT_DAYS} jours, suivi dans Flotte → Opérations & sinistres.${choiceIndex === 1 ? ' Enquête privée : −5 000 €.' : ''}`,
            type: 'event_resolved_cata',
          };
        }
      }
    }
    if (event.id === 'evt-cata-03') {
      // Vol de marchandise — cible un contrat actif avec véhicule assigné.
      const served = (state.activeContracts || []).filter(c => c.vehicleId);
      const target = served[Math.floor(Math.random() * served.length)];
      if (target) {
        const cargoLoss = Math.round((event.costMin || 8000) + Math.random() * ((event.costMax || 18000) - (event.costMin || 8000)));
        const vehicle = newVehicles.find(v => v.id === target.vehicleId);
        const coverage = vehicle?.insuranceCoverage || 'standard';
        const negotiated = choiceIndex === 1;
        const basePayable = negotiated ? Math.round(cargoLoss * 0.60) : cargoLoss;
        const rate = CATA_CARGO_COVERAGE[coverage] || 0;
        const reimbursement = Math.round(basePayable * rate);
        const playerPays = basePayable - reimbursement;
        newTreasury -= playerPays;
        outcomeNotice = {
          title: `Vol de marchandise — ${target.clientName || target.name}`,
          message: `Cargaison dérobée (préjudice ${cargoLoss.toLocaleString('fr-FR')} €). ${negotiated ? 'Dédommagement négocié à 60 %. ' : ''}Couverture ${coverage} : assureur ${reimbursement > 0 ? `rembourse ${reimbursement.toLocaleString('fr-FR')} €` : 'ne couvre pas la marchandise'}. Reste à charge : −${playerPays.toLocaleString('fr-FR')} €.`,
          type: 'event_resolved_cata',
        };
      }
    }

    // ── STRIKE EVENTS — real impact wired to gameplay ───────────────────
    // Trigger the pedagogical milestone the first time any social/strike
    // event is resolved.
    if (event.id === 'evt-social-01' || event.id === 'evt-social-02' || event.id === 'evt-social-03') {
      if (typeof get().triggerMilestone === 'function') {
        try { get().triggerMilestone('first_strike', '/hr'); }
        catch (err) { console.warn('[milestone] first_strike trigger failed:', err); }
      }
    }
    // (1) evt-social-01 "Grève nationale des transports" — 2 days.
    //     choice 0 (Attendre) : 100 % revenue cut for 2 days.
    //     choice 1 (Tenter livraisons urgentes) : 50 % cut for 2 days
    //                                             (the existing +200 € rep gain
    //                                             from choice.cost/repChange is kept).
    let companyStrikePatch = null;
    let firedArchivePatch = null;
    if (event.id === 'evt-social-01') {
      const days = event.durationDays || 2;
      companyStrikePatch = {
        kind: 'national',
        label: 'Grève nationale des transports',
        revenuePenalty: choiceIndex === 0 ? 1.0 : 0.5,
        until: state.totalDays + days,
      };
    }
    // (2) evt-social-03 "Blocage routier" — 1 day.
    //     choice 0 (Attendre) : -30 % revenue for 1 day.
    //     choice 1 (Itinéraire alternatif) : -15 % revenue (cost already paid).
    if (event.id === 'evt-social-03') {
      const days = event.durationDays || 1;
      companyStrikePatch = {
        kind: 'blocking',
        label: 'Blocage routier',
        revenuePenalty: choiceIndex === 0 ? 0.30 : 0.15,
        until: state.totalDays + days,
      };
    }
    // 10/07/2026 — PHASE 4 macro-événements : effets structurels temporaires
    // (mêmes flags state que les scénarios poussés par le superviseur —
    // zfeBan / fuelShock / companyStrike / crueEvent).
    let macroPatch = null;
    if (choice?.appliesMacroEffect) {
      const fx = choice.appliesMacroEffect;
      const today = state.totalDays || 0;
      if (fx.type === 'zfe_ban') {
        macroPatch = { zfeBan: { endDay: today + (fx.durationDays || 90) } };
      } else if (fx.type === 'fuel_shock') {
        macroPatch = { fuelShock: { multiplier: 1 + (fx.magnitude || 0.30), endDay: today + (fx.durationDays || 180) } };
      } else if (fx.type === 'strike') {
        companyStrikePatch = {
          kind: 'national',
          label: event.name,
          revenuePenalty: fx.revenuePenalty != null ? fx.revenuePenalty : 1.0,
          until: today + (fx.durationDays || 6),
        };
      } else if (fx.type === 'depot_flood') {
        macroPatch = { crueEvent: { day: today, cost: choice.cost || 0 } };
        companyStrikePatch = {
          kind: 'flood',
          label: event.name,
          revenuePenalty: 1.0,
          until: today + (fx.closureDays || 3),
        };
      }
    }
    // Convention collective : hausse PERMANENTE des grilles chauffeurs.
    if (choice?.appliesSalaryGridIncrease) {
      const pct = choice.appliesSalaryGridIncrease;
      const satBoost = choice.salarySatisfactionBoost || 0;
      newDrivers = newDrivers.map(d => ({
        ...d,
        salaryGross: Math.round((d.salaryGross || 0) * (1 + pct)),
        employerCost: Math.round((d.employerCost || 0) * (1 + pct)),
        satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + satBoost)),
      }));
    }
    // (3) evt-social-02 "Grève individuelle chauffeur" — driver-targeted.
    //     choice 0 (Céder) : +200 €/mois on the driver's salary (already deducted from treasury via choice.cost).
    //                       Apply the raise directly to the targeted driver.
    //     choice 1 (Négocier) : driver in 'striking' status for 3 days, vehicle freed.
    //     choice 2 (Refuser) : driver resigns (removed from drivers list).
    if (event.id === 'evt-social-02') {
      // 23/07/2026 — fix « chauffeur en formation qui disparaît » : plus de
      // fallback newDrivers[0] (grève/démission appliquée à un chauffeur en
      // formation). Sans cible éligible, le conflit social reste sans effet.
      const candidate = newDrivers.find(d => (d.status === 'idle' && !d.onHoliday) || d.status === 'active');
      if (candidate) {
        if (choiceIndex === 0) {
          // Yield: +200 €/mo raise on the targeted driver (employer cost +42 %).
          newDrivers = newDrivers.map(d => d.id === candidate.id ? {
            ...d,
            salaryGross: (d.salaryGross || 0) + 200,
            employerCost: Math.round((d.employerCost || 0) + 200 * 1.42),
            satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + 10)),
          } : d);
          newNotifications.push({
            id: generateId(),
            type: 'strike_resolved',
            title: 'Grève chauffeur résolue',
            message: `${candidate.name} obtient +200 €/mois. Conflit social terminé.`,
            day: state.totalDays,
            read: false,
          });
        } else if (choiceIndex === 1) {
          // Negotiate: driver on strike for 3 days, vehicle freed.
          const strikeDays = 3;
          if (candidate.assignedVehicleId) {
            newVehicles = newVehicles.map(v => v.id === candidate.assignedVehicleId ? {
              ...v,
              assignedDriverId: null,
              status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          newDrivers = newDrivers.map(d => d.id === candidate.id ? {
            ...d,
            status: 'striking',
            strikingUntil: state.totalDays + strikeDays,
            assignedVehicleId: null,
            satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + 3)),
          } : d);
          newNotifications.push({
            id: generateId(),
            type: 'strike_negotiation',
            title: 'Négociation en cours',
            message: `${candidate.name} est en grève. Reprise prévue dans ${strikeDays} jours.`,
            day: state.totalDays,
            read: false,
          });
        } else if (choiceIndex === 2) {
          // Refuse: driver resigns. Vehicle becomes idle.
          if (candidate.assignedVehicleId) {
            newVehicles = newVehicles.map(v => v.id === candidate.assignedVehicleId ? {
              ...v, assignedDriverId: null, status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          newDrivers = newDrivers.filter(d => d.id !== candidate.id);
          // 13/06/2026 — Archive the resigning driver so a future rehire
          // restores their trainings/level (bug : formations perdues).
          if (candidate.dataId) {
            firedArchivePatch = {
              ...(state.firedDriversArchive || {}),
              [candidate.dataId]: buildDriverArchiveEntry(candidate, state.totalDays),
            };
          }
          newNotifications.push({
            id: generateId(),
            type: 'strike_resignation',
            // 07/02/2026 — P0 fix : embarquer le nom dans le titre.
            title: `${candidate.name} démissionne (grève)`,
            message: `${candidate.name} a démissionné suite au conflit social. Recrutement requis.`,
            driverName: candidate.name,
            driverId: candidate.id,
            day: state.totalDays,
            read: false,
          });
        }
      }
    }

    // ── 17/06/2026 — Arc RH : Vol de carburant ─────────────────────────
    // Spawne l'étape suivante de l'arc selon le choix du joueur. L'arc
    // démarre sur `evt-rh-fuel-theft-detect` (probaMonth 4 %, gating
    // hasDrivers). Stages : detect → investigate → fire-cause / sanction
    // / mutual-end. À l'étape `detect`, le manque accumulé (3-8 K€ sur
    // 6 mois) est immédiatement passé en perte.
    let pendingArcEvent = null;
    let updatedFiredArchive = firedArchivePatch || null;
    // 17/06/2026 (Lot 4) — patch des compteurs annuels du yearly digest.
    // On accumule ici les bumps de l'événement courant (fuel theft résolu,
    // véhicule réformé) et on les flushe via le set() final.
    const yearlyCountersPatch = {};
    const bumpYearlyCounter = (key) => {
      yearlyCountersPatch[key] = (yearlyCountersPatch[key] != null
        ? yearlyCountersPatch[key]
        : (state[key] || 0)) + 1;
    };
    if (event.arc === 'fuel-theft') {
      // Étape `detect` : on encaisse la perte sèche (3 à 8 K€), même si
      // le joueur décide d'ignorer (le voleur a déjà détourné le manque).
      if (event.arcStage === 'detect') {
        const stolen = 3000 + Math.floor(Math.random() * 5000); // 3-8 K€
        newTreasury -= stolen;
        if (!outcomeNotice) {
          outcomeNotice = {
            title: 'Carburant : manque constaté',
            message: choice.arcAdvance === 'investigate'
              ? `Manque chiffré à ${stolen.toLocaleString('fr-FR')} € sur 6 mois. Audit interne lancé.`
              : `Le manque (${stolen.toLocaleString('fr-FR')} €) est passé en pertes. Le détourneur reste impuni.`,
            type: 'event_resolved_arc',
          };
        }
      }
      if (choice.arcAdvance) {
        const stage = choice.arcAdvance;
        if (stage === 'investigate') {
          // Audit lancé → résultat dans 7 à 14 jours.
          const nextEvt = EVENTS.find(e => e.id === 'evt-rh-fuel-theft-investigate');
          if (nextEvt) {
            pendingArcEvent = {
              ...nextEvt,
              instanceId: generateId(),
              resolvedAt: null,
              scheduledFor: (state.totalDays || 0) + 7 + Math.floor(Math.random() * 7),
            };
          }
        } else if (stage === 'ignore') {
          // Détourneur récidive : nouvelle alerte 30-60 j plus tard, montant doublé.
          const nextEvt = EVENTS.find(e => e.id === 'evt-rh-fuel-theft-detect');
          if (nextEvt) {
            pendingArcEvent = {
              ...nextEvt,
              instanceId: generateId(),
              resolvedAt: null,
              scheduledFor: (state.totalDays || 0) + 30 + Math.floor(Math.random() * 30),
              costMin: (nextEvt.costMin || 800) * 2,
              costMax: (nextEvt.costMax || 2200) * 2,
              name: 'Récidive : vol de carburant aggravé',
            };
          }
        } else if (stage === 'fire-cause') {
          // Licenciement faute grave : on identifie un chauffeur (le plus
          // ancien parmi idle/active) et on le sort de l'effectif.
          const suspect = newDrivers.find(d => d.status === 'idle' || d.status === 'active');
          let firedName = 'le salarié';
          if (suspect) {
            firedName = suspect.name;
            if (suspect.assignedVehicleId) {
              newVehicles = newVehicles.map(v => v.id === suspect.assignedVehicleId ? {
                ...v, assignedDriverId: null, status: v.status === 'active' ? 'idle' : v.status,
              } : v);
            }
            // Archive le chauffeur pour préserver les formations en cas de réembauche.
            if (suspect.dataId) {
              updatedFiredArchive = {
                ...(updatedFiredArchive || state.firedDriversArchive || {}),
                [suspect.dataId]: buildDriverArchiveEntry(suspect, state.totalDays),
              };
            }
            newDrivers = newDrivers.filter(d => d.id !== suspect.id);
            // Compteur fuel-theft résolu (fire-cause).
            bumpYearlyCounter('fuelTheftsResolvedThisYear');
          }
          // 10 % risque prud'hommes (5-15 K€).
          if (Math.random() < 0.10) {
            const fine = 5000 + Math.floor(Math.random() * 10000);
            newTreasury -= fine;
            outcomeNotice = {
              title: 'Prud\'hommes : licenciement contesté',
              message: `${firedName} a saisi le conseil de prud'hommes. Indemnité prononcée : ${fine.toLocaleString('fr-FR')} €.`,
              type: 'event_resolved_arc',
            };
          } else {
            outcomeNotice = {
              title: 'Licenciement validé',
              message: `Faute grave constatée pour ${firedName} : sortie sans préavis ni indemnité. Pas de suite judiciaire.`,
              type: 'event_resolved_arc',
            };
          }
        } else if (stage === 'sanction') {
          // 30 % risque récidive 60-120 j plus tard.
          if (Math.random() < 0.30) {
            const nextEvt = EVENTS.find(e => e.id === 'evt-rh-fuel-theft-detect');
            if (nextEvt) {
              pendingArcEvent = {
                ...nextEvt,
                instanceId: generateId(),
                resolvedAt: null,
                scheduledFor: (state.totalDays || 0) + 60 + Math.floor(Math.random() * 60),
                name: 'Récidive après sanction',
              };
            }
          }
          outcomeNotice = outcomeNotice || {
            title: 'Sanction disciplinaire posée',
            message: 'Avertissement écrit + retenue sur salaire (6 mois). Le salarié reste, sous surveillance.',
            type: 'event_resolved_arc',
          };
        } else if (stage === 'mutual-end') {
          // Rupture conventionnelle : le suspect quitte la société.
          const suspect = newDrivers.find(d => d.status === 'idle' || d.status === 'active');
          if (suspect) {
            if (suspect.assignedVehicleId) {
              newVehicles = newVehicles.map(v => v.id === suspect.assignedVehicleId ? {
                ...v, assignedDriverId: null, status: v.status === 'active' ? 'idle' : v.status,
              } : v);
            }
            if (suspect.dataId) {
              updatedFiredArchive = {
                ...(updatedFiredArchive || state.firedDriversArchive || {}),
                [suspect.dataId]: buildDriverArchiveEntry(suspect, state.totalDays),
              };
            }
            newDrivers = newDrivers.filter(d => d.id !== suspect.id);
            // Compteur fuel-theft résolu (mutual-end).
            bumpYearlyCounter('fuelTheftsResolvedThisYear');
          }
          outcomeNotice = outcomeNotice || {
            title: 'Rupture conventionnelle signée',
            message: 'Transaction conclue, sortie immédiate de l\'effectif. Zéro risque juridique.',
            type: 'event_resolved_arc',
          };
        }
      }
    }

    // ── 17/06/2026 — Aléa Douanes (contestation) ──────────────────────
    if (event.id === 'evt-customs-01' && choiceIndex === 1) {
      // Avocat : 40 % de chance d'obtenir une réduction de moitié.
      const baseFine = Math.round((event.costMin + event.costMax) / 2);
      // 08/02/2026 — Q2 Lot D : succès contestation boosté par audit IT
      // (+15 % de proba si inv-it-backup-01 actif).
      const itBackup = !!(state.activeInvestments || {})['inv-it-backup-01'];
      const successProba = itBackup ? 0.55 : 0.40;
      if (Math.random() < successProba) {
        const reduction = Math.round(baseFine * 0.5);
        newTreasury += reduction;
        outcomeNotice = {
          title: 'Douanes : contestation gagnée',
          message: `L'avocat fiscaliste a obtenu une réduction de ${reduction.toLocaleString('fr-FR')} €${itBackup ? ' (proba +15 % grâce à l\'audit IT)' : ''}.`,
          type: 'event_resolved_customs',
        };
      } else {
        outcomeNotice = {
          title: 'Douanes : contestation perdue',
          message: 'L\'amende reste due en intégralité, frais d\'avocat en plus.',
          type: 'event_resolved_customs',
        };
      }
    }

    // ── 17/06/2026 — Rupture moteur catastrophique ────────────────────
    // Choix 0 : remplacer le bloc moteur (30 K€, 10 j d'immobilisation, usure
    //           moteur remise à zéro = wear −40 pts plafonné à 5).
    // Choix 1 : réformer le véhicule (épave, vente casse ~10 % de la valeur).
    // Choix 2 : réparation low-cost gamble (60 % succès / 40 % re-casse).
    if (event.id === 'evt-cata-engine-rupture') {
      // Cible : le véhicule possédé le plus usé (cohérent avec evt-cata-01).
      const ownedActive = newVehicles.filter(v => !v.isLeased && v.status !== 'sold' && v.status !== 'selling' && v.status !== 'delivery');
      const target = [...ownedActive].sort((a, b) => (b.wear || 0) - (a.wear || 0))[0];
      if (target) {
        if (choiceIndex === 0) {
          // Bloc moteur remplacé : usure remise à 5 %, immobilisation 10 j.
          const downDays = event.durationDays || 10;
          if (target.assignedDriverId) {
            newDrivers = newDrivers.map(d => d.id === target.assignedDriverId ? {
              ...d, assignedVehicleId: null, status: d.status === 'active' ? 'idle' : d.status,
            } : d);
          }
          newVehicles = newVehicles.map(v => v.id === target.id ? {
            ...v,
            wear: 5,
            status: 'maintenance',
            maintenanceUntil: state.totalDays + downDays,
            assignedDriverId: null,
            assignedContractId: null,
          } : v);
          outcomeNotice = {
            title: `Bloc moteur remplacé — ${target.brand} ${target.model}`,
            message: `Véhicule immobilisé ${downDays} jours pour remplacement. Usure moteur remise à 5 %.`,
            type: 'event_resolved_cata',
          };
        } else if (choiceIndex === 1) {
          // Réformer le véhicule : vente casse ~10 % de la valeur.
          const scrapValue = Math.round((target.price || 0) * 0.10);
          newTreasury += scrapValue;
          if (target.assignedDriverId) {
            newDrivers = newDrivers.map(d => d.id === target.assignedDriverId ? {
              ...d, assignedVehicleId: null, status: d.status === 'active' ? 'idle' : d.status,
            } : d);
          }
          newVehicles = newVehicles.filter(v => v.id !== target.id);
          // Compteur véhicule réformé pour Bruno Lavergne.
          bumpYearlyCounter('vehiclesScrappedThisYear');
          outcomeNotice = {
            title: `Véhicule réformé — ${target.brand} ${target.model}`,
            message: `Vente en casse pour ${scrapValue.toLocaleString('fr-FR')} €. Le ${target.brand} ${target.model} quitte le parc.`,
            type: 'event_resolved_cata',
          };
        } else if (choiceIndex === 2) {
          // Réparation low-cost gamble (60 % succès / 40 % re-casse).
          if (Math.random() < 0.40) {
            const extraCost = 22000; // 30 000 − 8 000 déjà payés
            newTreasury -= extraCost;
            // Re-casse forcée : véhicule réformé après échec.
            const scrapValue = Math.round((target.price || 0) * 0.10);
            newTreasury += scrapValue;
            if (target.assignedDriverId) {
              newDrivers = newDrivers.map(d => d.id === target.assignedDriverId ? {
                ...d, assignedVehicleId: null, status: d.status === 'active' ? 'idle' : d.status,
              } : d);
            }
            newVehicles = newVehicles.filter(v => v.id !== target.id);
            // Compteur véhicule réformé pour Bruno Lavergne (échec low-cost).
            bumpYearlyCounter('vehiclesScrappedThisYear');
            outcomeNotice = {
              title: 'Réparation low-cost : échec',
              message: `Le bricolage a tenu 2 semaines. Rupture finale, ${extraCost.toLocaleString('fr-FR')} € supplémentaires + casse forcée (récupération ${scrapValue.toLocaleString('fr-FR')} €).`,
              type: 'event_resolved_cata',
            };
          } else {
            // Succès : le moteur tient, usure laissée en l'état.
            newVehicles = newVehicles.map(v => v.id === target.id ? {
              ...v, wear: Math.min(100, (v.wear || 0)),
            } : v);
            outcomeNotice = {
              title: 'Réparation low-cost : OK',
              message: 'Le moteur tient le coup. Économie de 22 000 € par rapport au remplacement complet.',
              type: 'event_resolved_cata',
            };
          }
        }
      }
    }

    // (Ancien chemin choiceIndex === 2 sans cible — devient obsolète, géré
    // par le bloc ci-dessus quand un véhicule est trouvé.)
    if (event.id === 'evt-cata-engine-rupture' && choiceIndex === 2 && !outcomeNotice) {
      if (Math.random() < 0.40) {
        const extraCost = 22000;
        newTreasury -= extraCost;
        outcomeNotice = {
          title: 'Réparation low-cost : échec',
          message: `Le bricolage a tenu 2 semaines. Rupture finale, vous payez ${extraCost.toLocaleString('fr-FR')} € supplémentaires pour le remplacement complet.`,
          type: 'event_resolved_cata',
        };
      } else {
        outcomeNotice = {
          title: 'Réparation low-cost : OK',
          message: 'Le moteur tient le coup. Économie de 22 000 € par rapport au remplacement complet.',
          type: 'event_resolved_cata',
        };
      }
    }

    // Net cash impact (negative = expense, positive = income) for the
    // exceptional-charges ledger consumed by the Finance page.
    const netCash = -(choice.cost || 0) + (newTreasury - (state.treasury - (choice.cost || 0)) > 0 ? 0 : 0);
    const eventCost = choice.cost || 0;
    const eventBonus = (choice.bonusRevenue && newTreasury > state.treasury - (choice.cost || 0))
      ? (newTreasury - (state.treasury - (choice.cost || 0)))
      : 0;

    // Also bump monthlyExpenses so the running totals stay consistent with
    // what the player sees in the daily tick.
    const updatedMonthlyExpenses = (state.monthlyExpenses || 0) + Math.max(0, eventCost - eventBonus);

    // Surface the random-outcome result as a notification so the player sees
    // what was actually rolled (clean / minor / major) rather than just the
    // posture they picked.
    if (outcomeNotice) {
      newNotifications.push({
        id: generateId(),
        type: outcomeNotice.type,
        title: outcomeNotice.title,
        message: outcomeNotice.message,
        day: state.totalDays,
        read: false,
      });
    }

    // 08/02/2026 — Q2 Lot C : feedback explicite que l'event a injecté un
    // tender ad-hoc dans le board AO. Le joueur doit savoir quoi faire ensuite.
    if (adHocTenderInjected) {
      newNotifications.push({
        id: generateId(),
        type: 'tender_adhoc_injected',
        title: `Nouvel AO ajouté — ${adHocTenderInjected.clientName}`,
        message: `Direction page **Appels d'Offres** pour étudier la fiche et soumettre votre offre dans les 15 jours.`,
        day: state.totalDays,
        read: false,
      });
    }

    // 08/02/2026 — Q2 Lot D : message d'information sur la faute en cas
    // d'accident faultBased non imputable au chauffeur. Le joueur voit
    // pourquoi sa réputation n'a pas baissé (météo, usure équipement…).
    if (accidentFaultInfo && !accidentFaultInfo.atFault) {
      const reasonLabel = {
        weather: 'conditions météo défavorables (force majeure)',
        equipment: 'défaillance équipement (usure flotte > 70 %)',
        fatigue: 'fatigue chauffeur (responsabilité organisationnelle)',
        standard: 'circonstances atténuantes',
      }[accidentFaultInfo.reason] || 'circonstances atténuantes';
      newNotifications.push({
        id: generateId(),
        type: 'accident_no_fault',
        title: 'Accident — pas de faute reconnue',
        message: `Le chauffeur n'est pas en faute (${reasonLabel}). Aucun impact réputation, seuls les coûts matériels sont à votre charge.`,
        day: state.totalDays,
        read: false,
      });
    } else if (accidentFaultInfo && accidentFaultInfo.atFault) {
      newNotifications.push({
        id: generateId(),
        type: 'accident_at_fault',
        title: 'Accident — faute reconnue',
        message: `Le chauffeur est en faute. Impact réputation appliqué (réduit par vos fonctions support et l'audit IT).`,
        day: state.totalDays,
        read: false,
      });
    }

    // Compute the new pendingEvents list (filter out resolved + push any
    // arc continuation that should fire later, e.g. fuel-theft → investigate).
    const remainingPendingEvents = state.pendingEvents.filter(e => e.instanceId !== eventInstanceId);
    const nextPendingEvents = pendingArcEvent
      ? [...remainingPendingEvents, pendingArcEvent]
      : remainingPendingEvents;

    set({
      treasury: newTreasury,
      reputation: newReputation,
      vehicles: newVehicles,
      drivers: newDrivers,
      notifications: newNotifications.slice(-40),
      monthlyExpenses: updatedMonthlyExpenses,
      pendingEvents: nextPendingEvents,
      // 10/02/2026 (Lot J Bug 1) — Cancel le contrat lié au véhicule
      // en panne pour qu'il sorte de la liste de validation contrat.
      ...(breakdownContractToCancel
        ? {
            activeContracts: (state.activeContracts || []).filter(
              c => c.instanceId !== breakdownContractToCancel
            ),
          }
        : {}),
      ...(companyStrikePatch ? { companyStrike: companyStrikePatch } : {}),
      ...(fleetOpsPatch ? { fleetOpsLog: fleetOpsPatch } : {}),
      ...(macroPatch || {}),
      ...(resignationArchivePatch ? { firedDriversArchive: resignationArchivePatch } : {}),
      ...(updatedFiredArchive ? { firedDriversArchive: updatedFiredArchive } : {}),
      ...(marketBoomPatch ? { marketBoom: marketBoomPatch } : {}),
      // 13/07/2026 — arc « crédit véhicule » (confrère retraité).
      ...(vehicleFinancingPatch ? { pendingVehicleFinancing: vehicleFinancingPatch } : {}),
      // 17/06/2026 (Lot 4) — annual counters for support yearly digest.
      ...(yearlyCountersPatch || {}),
      eventLog: [...state.eventLog, {
        ...event,
        resolvedAt: { day: state.day, month: state.month, year: state.year, totalDays: state.totalDays },
        choiceMade: outcomeNotice ? `${choice.label} — ${outcomeNotice.title.replace('Contrôle DREAL : ', '')}` : choice.label,
        netCashImpact: eventBonus - eventCost,
        eventCost,
        eventBonus,
      }],
    });

    // 18/09/2026 — Accusé de réception assureur pour les dossiers ouverts
    // par cet événement (vol de camion) — l'indemnité arrive via tickFleetOps.
    if (fleetOpsPatch && typeof get().pushCustomMessage === 'function') {
      fleetOpsPatch.filter(op => op.kind === 'claim' && op.claimType === 'theft' && op.createdDay === state.totalDays && op.status === 'declared').forEach(op => {
        try {
          get().pushCustomMessage({
            senderType: 'supplier',
            senderName: 'AssurFlotte — Service sinistres',
            tone: 'formal',
            kind: 'info',
            subject: `Dossier vol n°${op.id.slice(-6).toUpperCase()} — ${op.vehicleLabel} : déclaration reçue`,
            body:
              `Bonjour,\n\nNous accusons réception de votre déclaration de **vol** concernant le **${op.vehicleLabel}**.\n\n` +
              `• Valeur vénale retenue : ${op.repairCost.toLocaleString('fr-FR')} €\n` +
              `• Couverture ${op.tier} (${Math.round((op.coverageRate || 0) * 100)} %) → indemnisation prévue **${op.amount.toLocaleString('fr-FR')} €**\n\n` +
              `Instruction du dossier dès demain ; versement prévu le **jour ${op.payoutDay}**. Suivi : Flotte → « Opérations & sinistres ».\n\n— AssurFlotte`,
            ttlDays: 21,
          });
        } catch (err) { console.warn('[eventSlice] theft claim message failed:', err); }
      });
    }

    // 13/07/2026 — Arc pédagogique « crédit véhicule » : mail finance
    // expliquant les étapes de la demande de crédit affecté aux banques.
    // 31/07/2026 — cohérence : expéditeur = DAF si recruté, sinon le
    // conseiller choisi, sinon anonyme (Tristan n'existe pas encore en
    // début de partie).
    if (vehicleFinancingPatch && typeof get().pushCustomMessage === 'function') {
      const financeSender = getFinanceSender(get());
      get().pushCustomMessage({
        senderType: 'advisor',
        senderName: financeSender.senderName,
        subject: `Financer le ${vehicleFinancingPatch.brand} ${vehicleFinancingPatch.model} du confrère — crédit véhicule`,
        body: `Bonjour,\n\nVous avez réservé le ${vehicleFinancingPatch.brand} ${vehicleFinancingPatch.model} du confrère qui part à la retraite (${vehicleFinancingPatch.price.toLocaleString('fr-FR')} €, remise incluse). Un achat comptant nous ferait dépasser le découvert autorisé — c'est exactement le cas où une entreprise finance par CRÉDIT AFFECTÉ : le prêt est gagé sur le véhicule, ce qui rassure la banque même sans 3 mois d'historique (c'est l'exception à la règle habituelle).\n\nComment procéder :\n1. Ouvrez la page BANQUE.\n2. En haut, le dossier spécial « Financement véhicule » liste les propositions des 3 banques.\n3. Comparez : TAUX, MENSUALITÉ et COÛT TOTAL du crédit — c'est la mensualité qui pèsera sur notre trésorerie chaque mois.\n4. Choisissez la durée (24, 36 ou 48 mois) puis acceptez la meilleure offre : l'achat se conclut automatiquement.\n\n⚠️ Le confrère ne garde le véhicule que 10 jours. Passé ce délai, il le vendra à un autre transporteur.\n\n${financeSender.signature}`,
        kind: 'warning',
        ttlDays: 10,
      });
    }
  },
});
